import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { Check, ChevronDown } from 'lucide-react'

export interface ListboxOption {
  id: string
  label: string
}

/**
 * A styled single-choice list: the shell's stand-in for a native <select>,
 * which it never uses. Arrow keys, Home and End move; Enter or Space picks;
 * a click picks.
 */
export function Listbox({
  options,
  selectedId,
  onSelect,
  label,
  autoFocus = false,
}: {
  options: ListboxOption[]
  selectedId: string | null
  onSelect: (id: string) => void
  /** The accessible name; not drawn. */
  label: string
  autoFocus?: boolean
}) {
  const base = useId()
  const list = useRef<HTMLUListElement>(null)
  const [active, setActive] = useState(() => Math.max(0, options.findIndex((o) => o.id === selectedId)))

  useEffect(() => {
    if (autoFocus) list.current?.focus()
  }, [autoFocus])

  useEffect(() => {
    list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const onKeyDown = (e: KeyboardEvent) => {
    const last = options.length - 1
    const move = (i: number) => { e.preventDefault(); setActive(Math.min(last, Math.max(0, i))) }
    if (e.key === 'ArrowDown') move(active + 1)
    else if (e.key === 'ArrowUp') move(active - 1)
    else if (e.key === 'Home') move(0)
    else if (e.key === 'End') move(last)
    else if ((e.key === 'Enter' || e.key === ' ') && options[active]) {
      e.preventDefault()
      onSelect(options[active].id)
    }
  }

  return (
    <ul
      ref={list}
      role="listbox"
      tabIndex={0}
      aria-label={label}
      aria-activedescendant={options[active] ? `${base}-${active}` : undefined}
      onKeyDown={onKeyDown}
      className="max-h-80 overflow-y-auto space-y-1 p-1 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {options.map((o, i) => {
        const selected = o.id === selectedId
        return (
          // The listbox takes the keys (aria-activedescendant); a click picks directly.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <li
            key={o.id}
            id={`${base}-${i}`}
            data-index={i}
            role="option"
            aria-selected={selected}
            onMouseEnter={() => setActive(i)}
            onClick={() => onSelect(o.id)}
            className={`flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
              selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border'
            } ${i === active ? 'bg-muted' : ''}`}
          >
            <span className="flex-1 min-w-0 truncate">{o.label}</span>
            {selected && <Check className="w-4 h-4 text-primary shrink-0" aria-hidden />}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * A button showing the current choice that opens a Listbox beneath it.
 * Escape, a pick or a click elsewhere closes it.
 */
export function ListboxButton({
  options,
  selectedId,
  onSelect,
  label,
  className = '',
  field = false,
}: {
  options: ListboxOption[]
  selectedId: string | null
  onSelect: (id: string) => void
  label: string
  className?: string
  /** Drawn as a form field, full width, rather than as a heading. */
  field?: boolean
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const current = options.find((o) => o.id === selectedId)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); button.current?.focus() }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={box} className={`relative min-w-0 ${className}`}>
      <button
        ref={button}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current?.label ?? 'none'}`}
        onClick={() => setOpen((o) => !o)}
        className={
          field
            ? 'flex w-full items-center justify-between gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-left'
            : 'flex items-center gap-1.5 rounded-md px-2 py-1 text-base font-semibold hover:bg-muted min-w-0 max-w-full'
        }
      >
        <span className="truncate">{current?.label ?? label}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute left-0 top-full mt-1 z-50 ${field ? 'w-full' : 'w-72'} max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-card p-1 shadow-xl`}>
          <Listbox
            options={options}
            selectedId={selectedId}
            label={label}
            autoFocus
            onSelect={(id) => {
              onSelect(id)
              setOpen(false)
              button.current?.focus()
            }}
          />
        </div>
      )}
    </div>
  )
}
